import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormControl, ValidatorFn, Validators } from '@angular/forms';
import { DwComponent, DwComponentType, DwExpressionService, DwMetaDataFormApi, DwMetaDataService, DwMetaDataServiceToken, DwSectionBaseComponent, DwSecurityUserService, DwUiConfigRegistryService, DwUIMetaDataConfig, DwUIMetaDataConfigToken, DwMetaDataFormStateService, DwContainerOptions, isEmptyObject, DwLookupInfo, deepCopy, DwFormActionInterceptor, DwActionPreference, DwFormActionConfig, DwFormActionInterceptResult, DwFormActionKeys, DwFormContextMode } from '@devwareapps/devware-cap';
import { map, distinctUntilChanged, debounceTime, mergeMap } from 'rxjs/operators';
import { values } from 'underscore';
import { WachterUserExt } from '../../../../shared/models/wachter-user.model';
import { EmployeeReviewCategories } from '../../meta-data/constants/employee-review-categories.enum';
import { EmployeeReviewQuestionSetupLevel } from '../../meta-data/constants/employee-review-question-setup-level.enum';
import { EmployeeReviewAnswerEntity, EmployeeReviewAnswerValueEntity, EmployeeReviewEntity, EmployeeReviewQuestionEntity, PerformanceManagementMetaDataItemNames, PerformanceManagementMetaDataService, EmployeeReviewQuestionSetupEntity, PerformanceManagementMetaDataLookups, EmployeeReviewSummaryEntity, RoEthosUserEntity } from '../../meta-data/performancemanagement-meta-data.service';
import { EmployeeReviewQuestionData } from '../../models/employee-review-question-data.model';
import { PerformanceManagementRepositoryService } from '../../services/performance-management-repository.service';
import { combineLatest, of } from 'rxjs';
import { EmployeeReviewAnalysis, PerformanceManagementReviewService } from '../../services/performance-management-review.service';
import { EmployeeReviewSummaryStatus } from '../../meta-data/constants/employee-review-summary-status.enum';


@DwComponent(
  {
    key: 'employee-review-sumary',
    name: 'Employee Review Summary',
    componentType: DwComponentType.formSection,
    isGlobal: false,
    parentItemName: PerformanceManagementMetaDataItemNames.EmployeeReviewSummary,
  }
)
@Component({
  selector: 'app-employee-review-summary',
  templateUrl: './employee-review-summary.component.html',
  styleUrls: ['./employee-review-summary.component.scss']
})
export class EmployeeReviewSummaryComponent extends DwSectionBaseComponent {
  relatedQuestionSetups: EmployeeReviewSummaryEntity[];

  reviewSummary: EmployeeReviewSummaryEntity;

  employeeReviews: EmployeeReviewEntity[];
  questionSetup: EmployeeReviewQuestionSetupEntity[];


  questionData: EmployeeReviewQuestionData;
  showFullReview: boolean;
  employeeReviewAnalysis: EmployeeReviewAnalysis;
  employeeReview: EmployeeReviewEntity;
  supervisorReview: EmployeeReviewEntity;
  employeeUser: RoEthosUserEntity;
  supervisorUser: RoEthosUserEntity;

  loading: boolean = false;
  analysisLoading: boolean = true;

  inPrintMode: boolean = false;

  constructor(private dwSecurityUserService: DwSecurityUserService,
    dwExpressionService: DwExpressionService,
    protected dwUiConfigRegistryService: DwUiConfigRegistryService,
    @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    private dwMetaDataFormStateService: DwMetaDataFormStateService,
    private perfMonRepo: PerformanceManagementRepositoryService,
    private perfMonReviewService: PerformanceManagementReviewService

  ) {
    super(dwExpressionService, dwUiConfigRegistryService, uiMetaDataConfig, dwMetaDataService);

    const md = new PerformanceManagementMetaDataService();
  }

  setupComponentComplete(): void {
    // setup questions once data is loaded
    this.runAfterFormDataLoaded(() => this.setupReview());

    this.inPrintMode = this.dwMetaDataFormStateService.state.formContext.contextMode == DwFormContextMode.print;
  }

  updateReview(review: EmployeeReviewEntity, isSupervisorReview: boolean) {
    this.reviewSummary = this.dwMetaDataFormStateService.state.formApi.getFormData();

    this.reviewSummary.EmployeeReview = this.reviewSummary.EmployeeReview || [];

    const existingReview = this.reviewSummary.EmployeeReview.find(r => !r.IsSupervisorReview);

    // Create a new review with only the field we want to include
    const newReview: EmployeeReviewEntity = {} as any;
    newReview.EmployeeReviewId = review.EmployeeReviewId;
    newReview.DueDate = review.DueDate;

    if (!existingReview) {
      this.reviewSummary.EmployeeReview.push(newReview);
    } else {
      const index = this.reviewSummary.EmployeeReview.indexOf(existingReview);

      this.reviewSummary.EmployeeReview[index] = newReview;
    }

    this.dwMetaDataFormStateService.state.formApi.setFormData(this.reviewSummary);
  }

  setupForm() {
    // Add any custom controls needed
    this.setupControl('ReviewNotes', this.reviewSummary?.ReviewNotes, false);
    this.setupControl('ShowGraph', this.reviewSummary?.ShowGraph, false);
  }

  setupControl(controlName: string, value: any, required?: boolean): AbstractControl {
    let control = this.formGroup.get(controlName);

    if (!control) {
      control = new FormControl(value);

      this.formGroup.addControl(controlName, control);

      if (required) {
        control.setValidators(Validators.required);
      } else {
        control.clearValidators();
      }
    }

    return control;
  }


  private setupReview() {
    this.reviewSummary = this.dwMetaDataFormStateService.state.formApi.getFormData();

    // 
    if (!this.reviewSummary?.EmployeeReviewSummaryId || !this.reviewSummary?.ReviewYear) {
      return;
    }

    this.setupForm();

    if (this.loading) {
      return;
    }

    this.loading = true;
    this.analysisLoading = true;

    this.showFullReview = this.reviewSummary.EmployeeReviewSummaryStatusId == EmployeeReviewSummaryStatus.readyForReview || this.reviewSummary.EmployeeReviewSummaryStatusId == EmployeeReviewSummaryStatus.complete;

    // Determine what to load based the status (load only what is needed for each case)
    let reviewsOb = this.perfMonRepo.getEmployeeReviews(this.reviewSummary.EmployeeReviewSummaryId, this.showFullReview);
    const trustUsersOb = this.perfMonRepo.getEthosUsersInfo(this.reviewSummary.EmployeeEthosUserId, this.reviewSummary.SupervisorEthosUserId);

    let questionSetupOb = of(null);
    let questionDataOb = of(null);


    if (this.showFullReview) {
      questionSetupOb = this.perfMonRepo.getQuestionSetupForEval(this.reviewSummary.ReviewYear, this.reviewSummary.EmployeeEthosUserId);
      questionDataOb = this.perfMonRepo.getEmployeeReviewQuestions();

      this.setupControl('ReviewNotes', this.reviewSummary?.ReviewNotes, true);
    }

    return combineLatest([reviewsOb, trustUsersOb])
      .subscribe(([reviews, trustUsers]) => {
        this.employeeReviews = reviews;

        // Separate out employee and supervsior reviews
        this.employeeReview = reviews.find(r => !r.IsSupervisorReview);
        this.employeeUser = trustUsers.find(u => u.UserId == this.reviewSummary.EmployeeEthosUserId);

        this.supervisorReview = reviews.find(r => r.IsSupervisorReview);
        this.supervisorUser = trustUsers.find(u => u.UserId == this.reviewSummary.SupervisorEthosUserId);

        // trigger this when we're down loading reviews
        this.loading = false;

        if (!this.showFullReview) {
          this.analysisLoading = false;
          return;
        }

        // Get the questions for the Employee User
        const questionSetupOb = this.perfMonRepo.getQuestionSetupForEvalWithTrustUser(this.reviewSummary.ReviewYear, this.employeeUser);

        const questionDataOb = this.perfMonRepo.getEmployeeReviewQuestions();

        combineLatest([questionSetupOb, questionDataOb])
          .subscribe(([questionSetup, questionData]) => {
            this.questionSetup = questionSetup;
            this.questionData = questionData;

            this.employeeReviewAnalysis = this.perfMonReviewService.processEmployeeReviewAnalysis(this.employeeReviews, this.questionSetup, this.questionData);

            this.analysisLoading = false;
          });
      });

  }

}
